// web-v2/src/lib/quoteFields.js
//
// The quote form's field vocabulary: the picklists a carrier quote is entered
// against, and the pure helpers that parse and format what the broker types.
//
// LIFTED VERBATIM out of QuoteEntrySheet.jsx, not rewritten. Every function here
// was debugged in place over 38 iterations of the quoting UI and each one has a
// reason recorded next to it. QUOTE-WIZARD-SPEC phase 1 adds a second surface
// (QuoteWizardSheet) that asks the same questions in a different shape, and two
// copies of parseDeductible is how "5%" starts meaning different things on two
// screens. One home, both importers, no behaviour change.
//
// Pure and dependency-free (no React, no DOM) so a node test can import it.
// The JSX primitives that go with it live in components/quote/QuoteControls.jsx.

export const PART_TYPE_PICKLIST = [
    'CGL (General Liability)',
    'Negligent Instruction',
    'Hangarkeepers',
    'Products & Completed Ops',
    'Instructor Non-Owned',
    'Premises Liability',
    'Workers Comp',
    'TRIA Hull',
    'TRIA Liability',
    'War Hull',
    'War Liability',
    'Mexican Liability',
    'Dry Lease',
    'Other',
];

// COVERAGE SCOPE: four canonical values and an alias map, both DATA
// (data/coverage-types.json), resolved by utils/coverage-canon.js.
//
// DELIBERATE FRONTEND MIRROR of the canonical LIST ONLY, because web-v2 does not
// import repo-root utils. Same arrangement quoteCompleteness.js has with
// utils/coverage-type.js. tests/coverage-canon-frontend.test.js pins this list
// against data/coverage-types.json so an edit over there fails the gate here.
//
// THE ALIAS MAP IS NOT MIRRORED, on purpose. Resolution happens server-side, on
// the way in and on the way out, so there is ONE resolver. A second copy of the
// map in the browser is the second vocabulary this whole change exists to end.
//
// ORDER IS WHAT HE SELLS, not alphabetical: Full Flight (260 rows once aliases
// resolve), Ground Not in Motion (7), Liability Only (0 stored but a live engine
// path and a normal offer on older hulls), Ground and Taxi (0, and he expects
// that to stay true). Same principle as the subjectivity chips deleted in wave 1.
//
// 'All Risk' IS NOT HERE. David: "I don't want 'All Risk'", and separately
// "All Risk = Full Flight = Ground and Flight". It is a phrase carriers use on
// their paper, so it is an ALIAS and never a value. It was added to this list in
// wave 1 so a stored row could be reselected; coverageTypeOptions below is what
// keeps that working now, without the value.
export const COVERAGE_TYPE_OPTIONS = [
    'Full Flight',
    'Ground, Not in Motion',
    'Liability Only',
    'Ground and Taxi',
];

// The picklist as one particular quote should see it: the four canonical
// entries, plus this quote's own stored value when it is neither blank nor
// something the canonical list already covers.
//
// Nothing is rewritten and nothing is normalised on screen. A legacy
// 'Full Flight (Ground & Flight)' stays exactly that string, stays selected and
// stays visible. Wave 1 fixed a select that rendered BLANK on a stored value and
// that must not come back, which is why this appends rather than replaces.
export function coverageTypeOptions(stored) {
    const v = (stored == null ? '' : String(stored)).trim();
    if (!v || COVERAGE_TYPE_OPTIONS.includes(v)) return COVERAGE_TYPE_OPTIONS;
    return [...COVERAGE_TYPE_OPTIONS, v];
}

// A DELIBERATE MIRROR of utils/quote-card.js optionCardLabel's junk rules
// (web-v2 does not import repo-root utils; same arrangement quoteCompleteness.js
// has with utils/coverage-type.js, and tests/present-label-vocabulary.test.js
// pins the two together).
//
// Two labels are noise rather than information: the literal 'Option', which is
// what four writers used to stamp when a quote arrived without a real label,
// and a label that merely repeats the coverage type it sits next to. Both make
// a row read as though the software filled in a blank for itself.
//
// The card's `presentedCount > 1` gate is NOT mirrored, on purpose. That gate
// exists because a lone pill on a client card has nothing to disambiguate. The
// present sheet is the broker choosing BETWEEN options, so a real sibling label
// earns its place even when only one is ticked.
export function optionRowLabel(optionLabel, coverageType) {
    const label = (optionLabel == null ? '' : String(optionLabel)).trim();
    if (!label || label === 'Option') return '';
    const cov = (coverageType == null ? '' : String(coverageType)).trim();
    if (cov && label.toLowerCase() === cov.toLowerCase()) return '';
    return label;
}

export const OPW_CERTS = ['Private', 'Commercial', 'ATP', 'Other'];
export const OPW_RATINGS = ['Instrument', 'Multi', 'Sea', 'Glider', 'Helicopter', 'Tailwheel'];
// 12 is the default. 6 is a per-carrier exception (Beacon writes them). 24 was
// dropped 2026-07-30: nothing in the book has ever written one, and a term that
// is never used is a mis-click that reaches a policy end date. 'Other' stays for
// the genuine oddity.
export const TERM_MONTHS = ['12', '6', 'Other'];
// QUICK PICKS ARE SUGGESTIONS, NEVER THE LIST (David 2026-11-03: "i need option to type in any number for all the
// terms. quick selects are fine, but for medpay specifically 1000 and 3000 options can exist"). Every money term on a
// broker sheet is typed (TermAmountField / MoneyInput); these only save keystrokes. One home for every list.
export const MEDICAL_QUICK = [1000, 3000, 5000, 10000, 25000];
export const LIAB_OCC_QUICK = [1000000, 2000000, 5000000];
export const LIAB_PAX_QUICK = [100000, 200000, 250000];
// The deal's stated coverage terms (pre-stage renewal and roster sheets). $0 is a pick of its own where the sheet
// always offered it: a stated nil is an answer, distinct from "not stated" (Clear).
export const COVERAGE_OCC_QUICK = [100000, 1000000, 2000000, 5000000, 10000000, 20000000, 25000000];
export const COVERAGE_PAX_QUICK = [0, 25000, 100000, 250000, 500000];
export const COVERAGE_MED_QUICK = [0, ...MEDICAL_QUICK];

// What a typed money term holds: a whole, non-negative dollar amount, or null for "not stated". Any amount is
// accepted (no list). "$3,000", "3000" and "3,000.00" are 3000 (cents dropped, whole dollars); "0" and "$0" are 0,
// a real answer; blank or "$" alone is null. Text that is not a plain amount ("-500", "1e6", "25x") is REFUSED as
// null, never repaired into a different number (review 2026-11-03: "-500" had become 500 and "1e6" 16). Blank never
// becomes 0 and 0 never becomes blank. The server copy is utils/hangar-terms.js termAmount; keep the two the same.
export const TERM_AMOUNT_TEXT = /^\$?\s*(\d[\d,]*)(\.\d*)?$/;
export function parseTermAmount(raw) {
    if (raw == null) return null;
    if (typeof raw === 'number') return Number.isFinite(raw) && raw >= 0 ? Math.trunc(raw) : null;
    const m = String(raw).trim().match(TERM_AMOUNT_TEXT);
    return m ? Number(m[1].replace(/,/g, '')) : null;
}

// There were four subjectivity quick-buttons here. David, 2026-07-30, after a
// full day of quote entry: "never gonna use any of those subjectivities listed
// as quick buttons." They were a guess at what a carrier might impose, offered
// to the one broker who would know, and he does not want them. The textarea on
// both sheets is unchanged; only the guesses are gone.

// Stable key for an optional coverage part so the client's election survives
// round-trips (falls back to a slug of the part type). Matches the jsonb `key`.
export function coveragePartKey(r) {
    if (r && r.key) return r.key;
    return String(r && r.part_type || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

export function toNumber(v) {
    if (v == null || v === '') return null;
    const n = Number(String(v).replace(/[,$\s]/g, ''));
    return Number.isFinite(n) ? n : null;
}

export function fmtMoney(n) {
    if (n == null || !Number.isFinite(n)) return '';
    return '$' + Number(n).toLocaleString('en-US');
}

// Tail-number match key. Scenario aircraft_hull / includedTails (4b-2) key
// by tail string, prefill aircraft by tail_display. Normalize both sides.
export function normTail(v) {
    return String(v || '').trim().toUpperCase();
}

// Deductible inputs are plain text so brokers can enter "5%" or "$2,500".
// parseDeductible classifies the entry; deductiblePreview formats it back
// as a live $/% confirmation under the input.
// A STATED ZERO IS AN ANSWER, AND IT IS THE BEST ONE A CARRIER CAN GIVE.
//
// This returned null for '0' from 2026-08-01 until 2026-08-05, on the belief
// that a '0' in a TEXT column meant "nobody stated it". THE PREMISE WAS WRONG,
// and the live data says so plainly: NOTHING WRITES '0' AS A DEFAULT (every
// writer defaults to '' or NULL), and unknown is spelled NULL — 217 of 368
// option rows. The zeros are typed: 30 rows carry ZERO/ZERO and one ZERO/VALUE,
// the newest entered 2026-08-04.
//
// The cost of the old reading: David entered $0 / $0 on Starr's quote for
// N8095M and TRACON printed "deductible not stated" on the sheet he presents
// from. Starr at $3,328 beats Beacon at $3,338 by ten dollars AND by a nil
// deductible on a $175,000 hull, and the nil was the part being erased.
//
// It also destroyed a distinction this codebase has a rule about: "not stated"
// is a real, separate state (utils/coverage-canon.js:43, "a quote that never
// stated a coverage has not stated one"). Rendering a stated zero as that state
// is a placeholder and an answer sharing one spelling, in reverse.
//
// NULL and '' remain not-stated. Zero in any spelling is an amount.
export function parseDeductible(v) {
    if (v == null || v === '') return null;
    const s = String(v).trim();
    const pct = s.match(/^(\d+(?:\.\d+)?)\s*%$/);
    if (pct) {
        const n = Number(pct[1]);
        return Number.isFinite(n) && n >= 0 ? { kind: 'pct', value: n } : null;
    }
    // Zero arrives spelled several ways: 16 live rows carry '0.00' or '$0'
    // rather than '0'. The strip + Number() handles all of them, so the guard
    // is >= 0 and never a string comparison against one spelling.
    const n = Number(s.replace(/[,$\s]/g, ''));
    return Number.isFinite(n) && n >= 0 ? { kind: 'flat', value: n } : null;
}

export function deductiblePreview(v) {
    const p = parseDeductible(v);
    if (!p) return null;
    return p.kind === 'pct' ? `→ ${p.value}% of hull` : `→ ${fmtMoney(p.value)}`;
}

// One stored deductible as the client should read it: '$2,500', '5%' or '$0'.
// Returns null ONLY when nothing was stated (NULL / ''). A stated zero formats
// as '$0' — see parseDeductible for why that reversed on 2026-08-05.
export function deductibleAmount(v) {
    const p = parseDeductible(v);
    if (!p) return null;
    return p.kind === 'pct' ? `${p.value}%` : fmtMoney(p.value);
}

const GNIM = 'Ground, Not in Motion';

/**
 * The deductible line for one option row.
 *
 * NOT IN MOTION LEADS. The pair was rendering in-motion-first and unlabelled,
 * so Beacon's own '$250 Not In Motion / $2,500 In Motion' came back as
 * 'ded 2500/250'. Right data, reversed, and ambiguous to anyone who is not
 * David. Both sides are labelled now.
 *
 * GNIM has no in-motion deductible because it has no in-motion COVERAGE, so
 * the absence is a fact about the quote rather than a gap in the record. All 3
 * half-pair rows in the corpus are GNIM and nothing else is (live 2026-08-01),
 * which is David's rule holding without exception.
 *
 * `coverageCanonical` is resolved SERVER-side by utils/coverage-canon.js, the
 * one resolver, and passed down. web-v2 does not import repo-root utils and
 * this deliberately does not become a fourth place that knows how to spell
 * GNIM: 'Option C - GNIM' and 'Ground Not in Motion' both have to land here as
 * the same thing, and only the resolver knows that.
 */
// PRESENCE IS `!= null`, NEVER TRUTHINESS. deductibleAmount returns '$0' for a
// stated nil, and while a non-empty string is truthy today, a truthiness test
// here is one fmtMoney change away from erasing a zero again. Zero is a real
// answer for every money field in this file.
export function deductibleLine(o) {
    const nim = deductibleAmount(o && o.deductible_not_in_motion);
    const im = deductibleAmount(o && o.deductible_in_motion);
    if ((o && o.coverage_canonical) === GNIM) {
        return `${nim != null ? `${nim} NIM` : 'deductible not stated'} · no in-motion coverage`;
    }
    if (nim == null && im == null) return 'deductible not stated';
    return [nim != null ? `${nim} NIM` : null, im != null ? `${im} IM` : null].filter(Boolean).join(' / ');
}

// Quote ref, term and subjectivities used to be serialized into broker_notes as
// bracketed prose and regex-parsed back out, because the columns did not exist
// when the entry sheet was written. They exist now (quotes.quote_number,
// quotes.term_months, quotes.subject_to), so the payload builders send them to
// the columns and NOTHING is packed into notes any more.
//
// [QDate] IS THE ONE EXCEPTION AND IT IS DELIBERATE. The carrier's quote date
// still has no column. `received_at` is when WE created the row, not when the
// carrier responded, so mapping to it would overwrite a broker's answer with an
// ingestion timestamp (see e96af22, which left Quote_Date unmapped in the CRM
// mirror for exactly this reason). Homeless data is flagged as homeless rather
// than parked somewhere adjacent, so it stays visibly in prose until it gets a
// column of its own.
export function buildBrokerNotes({ baseNotes, qDate }) {
    const lines = [];
    if (qDate) lines.push(`[QDate: ${qDate}]`);
    if (baseNotes && baseNotes.trim()) lines.push(baseNotes.trim());
    return lines.join('\n\n');
}

export function splitBrokerNotes(combined) {
    const result = { baseNotes: '', ref: '', qDate: '', qExp: '', term: '12', subjectivities: '' };
    if (!combined) return result;
    let remaining = String(combined);
    const refMatch = remaining.match(/\[Ref:\s*([^\]]+)\]/);
    if (refMatch) { result.ref = refMatch[1].trim(); remaining = remaining.replace(refMatch[0], '').trim(); }
    const qDateMatch = remaining.match(/\[QDate:\s*([^\]]+)\]/);
    if (qDateMatch) { result.qDate = qDateMatch[1].trim(); remaining = remaining.replace(qDateMatch[0], '').trim(); }
    const qExpMatch = remaining.match(/\[QExp:\s*([^\]]+)\]/);
    if (qExpMatch) { result.qExp = qExpMatch[1].trim(); remaining = remaining.replace(qExpMatch[0], '').trim(); }
    const termMatch = remaining.match(/\[Term:\s*(\d+)/);
    if (termMatch) { result.term = termMatch[1]; remaining = remaining.replace(/\[Term:[^\]]+\]/, '').trim(); }
    const subjMatch = remaining.match(/\[Subj\]\s*\n?([\s\S]*?)(?=\n\n|$)/);
    if (subjMatch) { result.subjectivities = subjMatch[1].trim(); remaining = remaining.replace(subjMatch[0], '').trim(); }
    result.baseNotes = remaining.trim();
    return result;
}

// APR marker format for the single CRM Quotation.Pilot_Requirements text:
//
//   {common text, applies to all pilots}
//
//   [Nathan Miller]
//   {nathan-specific text}
//
//   [Bruce Casner]
//   {bruce-specific text}
//
// Round-trips losslessly when only `common` is set (no markers emitted, no
// markers found on parse). bind-from-quotation parses the same format on
// the server side to write each pilot's Approved_Pilots row individually.
// THE COLLISION, AND THE ESCAPE (item 6c, 2026-07-30). The marker is "a line
// that is nothing but a bracketed string", and a broker pasting a carrier's
// wording can produce one by accident:
//
//     before solo:
//     [SEE ENDORSEMENT]      <- read as a pilot named SEE ENDORSEMENT
//     25 hrs dual            <- reassigned to him
//
// Everything after it moved to a pilot who does not exist, and that prints on a
// carrier application.
//
// The escape is ONE LEADING SPACE on any body line that would otherwise read as
// a marker. It was chosen for what happens when something that does not know
// about it reads the field: both marker regexes anchor the bracket at column 0,
// so an indented line is already inert, and the worst case is a single
// invisible space on a line of a PDF. A backslash escape would have printed a
// backslash onto paper a client signs.
//
// Ordinary text is untouched, so nothing stored changes shape, and text already
// in the old format still parses (no migration).
const MARKER_LINE = /^\[[^\]\n]*\]\s*$/;

function escapeAprBody(text) {
    return String(text || '').split('\n')
        .map((line) => (MARKER_LINE.test(line) ? ` ${line}` : line))
        .join('\n');
}

function unescapeAprBody(text) {
    return String(text || '').split('\n')
        .map((line) => (line.startsWith(' ') && MARKER_LINE.test(line.slice(1)) ? line.slice(1) : line))
        .join('\n');
}

export function parsePilotRequirements(text) {
    if (!text || !text.trim()) return { common: '', byName: {} };
    const out = { common: '', byName: {} };
    const re = /^\[([^\]\n]+)\]\s*$/gm;
    const matches = [];
    let m;
    while ((m = re.exec(text)) !== null) {
        matches.push({ name: m[1].trim(), start: m.index, end: m.index + m[0].length });
    }
    if (matches.length === 0) {
        out.common = unescapeAprBody(text.trim());
        return out;
    }
    out.common = unescapeAprBody(text.slice(0, matches[0].start).trim());
    for (let i = 0; i < matches.length; i++) {
        const block = matches[i];
        const nextStart = matches[i + 1] ? matches[i + 1].start : text.length;
        out.byName[block.name] = unescapeAprBody(text.slice(block.end, nextStart).trim());
    }
    return out;
}

// The COMMON REGION of the text above, built from the structured shared
// requirements rather than typed. Deliberate frontend mirror of
// buildCommonRollup in utils/common-requirements.js (web-v2 does not import
// repo-root utils; same arrangement as the hull-scope mirror in
// quoteCompleteness.js). tests/common-requirements.test.js pins the two
// together so an edit over there fails the gate here.
//
// The column is canonical and this is its display copy, exactly the arrangement
// utils/apr-rollup.js already uses for the per-pilot matrix. Emits nothing that
// could be mistaken for a [Pilot Name] marker.
// Calendar-exact term math. Frontend mirror of utils/policy-term.js (web-v2 does
// not import repo-root utils). Drives the day-count pro-rata HINT the offer step
// offers for a six-month premium: 184/365, not half, because carriers price off
// the actual day count.
//
// Moved here from QuoteEntrySheet 2026-07-31 so both quote surfaces share one
// copy. Two copies of date arithmetic is two answers waiting to differ.
export function addMonthsClamped(ymd, months) {
    const [y, m, d] = ymd.split('-').map(Number);
    const lastDay = new Date(y, m - 1 + months + 1, 0).getDate();
    const target = new Date(y, m - 1 + months, Math.min(d, lastDay));
    return [target.getFullYear(),
        String(target.getMonth() + 1).padStart(2, '0'),
        String(target.getDate()).padStart(2, '0')].join('-');
}

export function daysBetweenISO(a, b) {
    const [ay, am, ad] = a.split('-').map(Number);
    const [by, bm, bd] = b.split('-').map(Number);
    return Math.round((Date.UTC(by, bm - 1, bd, 12) - Date.UTC(ay, am - 1, ad, 12)) / 86400000);
}

export function buildCommonRollup(rows) {
    return (Array.isArray(rows) ? rows : [])
        .map((r) => ({
            tail_number: String(r?.tail_number || '').trim().toUpperCase(),
            requirement: String(r?.requirement || '').trim(),
        }))
        .filter((r) => r.requirement)
        .map((r) => (r.tail_number
            ? `As relates ${r.tail_number}, all pilots: ${r.requirement}`
            : `All aircraft: ${r.requirement}`))
        .join('\n');
}

export function encodePilotRequirements(common, byNameMap, pilotsList) {
    const parts = [];
    const trimmedCommon = (common || '').trim();
    if (trimmedCommon) parts.push(escapeAprBody(trimmedCommon));
    for (const p of pilotsList) {
        const name = (p && p.name || '').trim();
        const specific = (byNameMap[name] || '').trim();
        if (name && specific) parts.push('[' + name + ']\n' + escapeAprBody(specific));
    }
    return parts.join('\n\n');
}

// Line endings, normalised on the way IN rather than at save time, because
// normalising at save leaves him looking at the doubled breaks he pasted until
// he reloads, which is the complaint (item 6a). Word and Outlook paste \r\n;
// nothing in this codebase ever stripped it.
//
// Three or more consecutive breaks collapse to two: a paragraph gap is worth
// keeping, a six-line hole from a pasted table is not.
export function normalizeMultiline(v) {
    return String(v ?? '').replace(/\r\n?/g, '\n').replace(/\n{3,}/g, '\n\n');
}
